import { useMemo, useRef, useState } from 'react';
import {
  createClientsBulk,
  useArchivedClients,
  useClients,
  useClientStats,
  useClientTiers,
} from '@busy/api';
import { ModalCloseButton, ModalShell, useToast } from '@busy/ui';
import {
  detectClientColumns,
  downloadCsv,
  formatBirthdayCell,
  formatCsvLine,
  parseCsv,
  phoneFingerprint,
  rowToClient,
  type ClientColumnMap,
} from '../../lib/csv';
import { useT } from '../../lib/i18n';
import { AlphaClientList } from '../clients/AlphaClientList';
import { ClientDetailModal } from '../ClientDetailModal';
import { NewClientModal } from '../NewClientModal';

// Settings → Clients panel. Three actions:
//   1. Download a sample CSV so the barber knows the column shape we accept
//   2. Import a CSV — preview + dedupe by phone before inserting
//   3. Export the current roster as a CSV (for backup or migration)
//
// Dedupe heuristic: phone fingerprint = last 10 digits, stripped. Two clients with
// matching fingerprints are treated as the same person — the import skips the
// duplicate. Clients without a phone in the CSV are inserted as new rows.
export function ClientsCsvPanel() {
  const clients = useClients();
  const archived = useArchivedClients();
  const stats = useClientStats();
  const tiers = useClientTiers();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [previewState, setPreviewState] = useState<PreviewState | null>(null);
  const [importing, setImporting] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState<string | null>(null);
  const [showNewClient, setShowNewClient] = useState(false);
  const toast = useToast();
  const t = useT();

  const statsByClient = useMemo(
    () => new Map((stats.data ?? []).map((s) => [s.client_id, s])),
    [stats.data],
  );
  const tierByClient = useMemo(
    () => new Map((tiers.data ?? []).map((tt) => [tt.client_id, tt.tier])),
    [tiers.data],
  );
  const selectedClient = useMemo(() => {
    const all = [...(clients.data ?? []), ...(archived.data ?? [])];
    return all.find((c) => c.id === selectedClientId) ?? null;
  }, [clients.data, archived.data, selectedClientId]);

  const existingPhoneFingerprints = useMemo(() => {
    const set = new Set<string>();
    (clients.data ?? []).forEach((c) => {
      const fp = phoneFingerprint(c.phone);
      if (fp) set.add(fp);
    });
    return set;
  }, [clients.data]);

  function handleSampleDownload() {
    // birthday_mmdd is optional — leaving it blank is fine. We use the
    // explicit "_mmdd" suffix in the header so anyone eyeballing the
    // file knows it's month/day only, never a year. (Booking-platform
    // headers like "dob" / "birth_date" are accepted on import — the
    // parser strips any year that's present.)
    const sample = [
      formatCsvLine(['first_name', 'last_name', 'phone', 'notes', 'birthday_mmdd']),
      formatCsvLine(['Marcus', 'Reyes', '5551234567', 'Tight on the sides, length on top.', '06/14']),
      formatCsvLine(['Tyrone', 'Brooks', '(555) 555-0173', 'Beard + line-up monthly.', '12/02']),
      formatCsvLine(['Eric', 'Hall', '', 'No phone on file yet.', '']),
    ].join('\n');
    downloadCsv('busy-barber-sample.csv', sample);
    toast(t('csv.toast.sample'), 'success');
  }

  function handleFilePicked(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result ?? '');
      processCsvText(text, file.name);
    };
    reader.onerror = () => toast(t('csv.toast.readFail'), 'error');
    reader.readAsText(file);
    // Reset so the same file can be re-picked if the user dismisses the preview
    e.target.value = '';
  }

  function processCsvText(text: string, fileName: string) {
    const { headers, rows } = parseCsv(text);
    if (rows.length === 0) {
      toast(t('csv.toast.noRows', { file: fileName }), 'error');
      return;
    }
    const map = detectClientColumns(headers);
    const hasNameCols = !!(map.first_name || map.last_name || map.name);
    if (!hasNameCols) {
      toast(t('csv.toast.noNameCol'), 'error');
      return;
    }

    const newClients: NewClientPreview[] = [];
    const duplicates: NewClientPreview[] = [];
    const skipped: number[] = []; // 1-based row numbers that had no name
    const seenInBatch = new Set<string>(); // dedupe within the import file itself

    rows.forEach((r, idx) => {
      const parsed = rowToClient(r, map);
      if (!parsed) {
        skipped.push(idx + 2); // +2: header + 1-based
        return;
      }
      const fp = phoneFingerprint(parsed.phone);
      const isDup = (fp && existingPhoneFingerprints.has(fp)) || (fp && seenInBatch.has(fp));
      if (fp) seenInBatch.add(fp);
      const preview: NewClientPreview = {
        rowNum: idx + 2,
        first_name: parsed.first_name,
        last_name: parsed.last_name,
        phone: parsed.phone,
        notes: parsed.notes,
        birthday_month: parsed.birthday_month,
        birthday_day: parsed.birthday_day,
        isDuplicate: !!isDup,
      };
      if (isDup) duplicates.push(preview);
      else newClients.push(preview);
    });

    setPreviewState({
      fileName,
      map,
      newClients,
      duplicates,
      skipped,
    });
  }

  async function handleConfirmImport() {
    if (!previewState) return;
    setImporting(true);
    const payload = previewState.newClients.map((c) => ({
      first_name: c.first_name,
      last_name: c.last_name,
      phone: c.phone,
      notes: c.notes,
      birthday_month: c.birthday_month,
      birthday_day: c.birthday_day,
    }));
    const result = await createClientsBulk(payload);
    setImporting(false);
    if (result.error) {
      toast(t('csv.toast.importFail', { message: result.error.message }), 'error');
      return;
    }
    const insertedCount = result.data?.length ?? payload.length;
    const skipped = result.skippedDuplicates ?? 0;
    toast(
      t(insertedCount === 1 ? 'csv.toast.imported_one' : 'csv.toast.imported_other', {
        count: insertedCount,
        file: previewState.fileName,
      }),
      'success',
    );
    if (skipped > 0) {
      toast(t('csv.toast.skippedDuplicates', { count: skipped }), 'info');
    }
    setPreviewState(null);
  }

  function handleExport() {
    const list = clients.data ?? [];
    if (list.length === 0) {
      toast(t('csv.toast.exportEmpty'), 'info');
      return;
    }
    // birthday_mmdd is appended LAST to keep existing column order stable
    // for anyone who's trained their eye on prior exports. Blank cell
    // when the client has no birthday on file. Format is always
    // zero-padded MM/DD so the round-trip is loss-free.
    const lines: string[] = [
      formatCsvLine([
        'first_name',
        'last_name',
        'phone',
        'notes',
        'created_at',
        'birthday_mmdd',
      ]),
    ];
    for (const c of list) {
      lines.push(
        formatCsvLine([
          c.first_name,
          c.last_name,
          c.phone,
          c.notes,
          c.created_at,
          formatBirthdayCell(c.birthday_month, c.birthday_day),
        ]),
      );
    }
    const today = new Date().toISOString().slice(0, 10);
    downloadCsv(`busy-barber-clients-${today}.csv`, lines.join('\n'));
    toast(
      t(list.length === 1 ? 'csv.toast.exported_one' : 'csv.toast.exported_other', {
        count: list.length,
      }),
      'success',
    );
  }

  return (
    <>
      <div className="space-y-5">
        {/* CSV import / export tools — pinned to the top so a barber on a
            fresh device can seed their roster from a backup before doing
            anything else with the list below. */}
        <section className="space-y-3">
          <h3 className="font-display text-lg">{t('csv.toolsHeading')}</h3>
          <p className="text-[14px] text-ink-700 leading-relaxed">{t('csv.intro')}</p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="h-11 rounded-lg bg-navy-900 text-white dark:bg-gold-500 dark:text-navy-900 font-semibold inline-flex items-center justify-center gap-2"
            >
              <UploadIcon className="w-4 h-4" />
              {t('csv.import')}
            </button>
            <button
              type="button"
              onClick={handleExport}
              className="h-11 rounded-lg border border-line bg-surface text-ink-900 font-semibold inline-flex items-center justify-center gap-2 hover:bg-bg-alt"
            >
              <DownloadIcon className="w-4 h-4" />
              {t('csv.export')}
            </button>
          </div>

          <button
            type="button"
            onClick={handleSampleDownload}
            className="text-xs font-bold text-ink-700 hover:underline inline-flex items-center gap-1"
          >
            <DownloadIcon className="w-3 h-3" />
            {t('csv.sample')}
          </button>

          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,text/csv"
            onChange={handleFilePicked}
            className="hidden"
          />

          <div className="rounded-xl bg-bg-alt border border-line p-3 text-[12px] text-ink-700 leading-relaxed">
            <strong className="block mb-1">{t('csv.recognizedHeaders')}</strong>
            <code className="text-ink-500">first_name, last_name, phone, notes, birthday_mmdd</code>
            <span className="block text-ink-500 mt-1">{t('csv.recognizedHelp')}</span>
            <span className="block text-ink-500 mt-1">{t('csv.birthdayHelp')}</span>
          </div>

          <div className="text-[12px] text-ink-500">
            {clients.data
              ? t(clients.data.length === 1 ? 'csv.rosterCount_one' : 'csv.rosterCount_other', {
                  count: clients.data.length,
                })
              : t('csv.rosterLoading')}
          </div>
        </section>

        {/* Roster — same Apple-Contacts-style list as the Clients route, so
            multi-select / archive / restore / delete works here too.
            maxHeight caps the list card so the Settings page can scroll
            naturally above + below it. */}
        <section className="pt-4 border-t border-line">
          <h3 className="font-display text-lg mb-2">{t('csv.rosterHeading')}</h3>
          <AlphaClientList
            onOpenClient={setSelectedClientId}
            onAddNewClient={() => setShowNewClient(true)}
            maxHeight="60vh"
          />
        </section>
      </div>

      {selectedClient && (
        <ClientDetailModal
          client={selectedClient}
          stats={statsByClient.get(selectedClient.id)}
          tier={tierByClient.get(selectedClient.id)}
          onClose={() => setSelectedClientId(null)}
        />
      )}

      {showNewClient && (
        <NewClientModal
          onClose={() => setShowNewClient(false)}
          onOpenExisting={(id) => setSelectedClientId(id)}
        />
      )}

      {previewState && (
        <ImportPreviewModal
          state={previewState}
          importing={importing}
          onConfirm={handleConfirmImport}
          onCancel={() => setPreviewState(null)}
        />
      )}
    </>
  );
}

type NewClientPreview = {
  rowNum: number;
  first_name: string;
  last_name: string;
  phone: string | null;
  notes: string | null;
  birthday_month: number | null;
  birthday_day: number | null;
  isDuplicate: boolean;
};

type PreviewState = {
  fileName: string;
  map: ClientColumnMap;
  newClients: NewClientPreview[];
  duplicates: NewClientPreview[];
  skipped: number[];
};

function ImportPreviewModal({
  state,
  importing,
  onConfirm,
  onCancel,
}: {
  state: PreviewState;
  importing: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const t = useT();
  const detectedFields = (Object.entries(state.map) as Array<[string, string | null]>)
    .filter(([, header]) => header !== null)
    .map(([field, header]) => ({ field, header: header as string }));

  const willInsert = state.newClients.length;
  const willSkipDup = state.duplicates.length;
  const willSkipBlank = state.skipped.length;

  const summaryKey =
    willSkipDup === 0
      ? 'csv.preview.summary_zeroDup'
      : willSkipDup === 1
      ? 'csv.preview.summary_oneDup'
      : 'csv.preview.summary_otherDup';
  const summary =
    t(summaryKey, { insert: willInsert, dup: willSkipDup }) +
    (willSkipBlank > 0 ? t('csv.preview.blankSuffix', { blank: willSkipBlank }) : '');

  return (
    <ModalShell open onClose={onCancel} size="lg" ariaLabel={t('csv.preview.aria')}>
      {/* flex-col frame so the Import / Cancel footer stays pinned even
          when the preview lists (new clients, duplicates) are long. */}
      <div className="flex flex-col max-h-[90dvh]">
      <ModalCloseButton onClose={onCancel} />
      <div className="shrink-0 p-5 pr-12 border-b border-line">
        <div className="text-[11px] tracking-[0.16em] uppercase font-extrabold text-ink-500 mb-1">
          {t('csv.preview.eyebrow', { file: state.fileName })}
        </div>
        <h2 className="font-display text-2xl">{summary}</h2>
      </div>

      <div className="flex-1 overflow-y-auto p-5 space-y-4 min-h-0">
        <section>
          <SectionLabel>{t('csv.preview.detectedCols')}</SectionLabel>
          <ul className="text-sm space-y-1 mt-1">
            {detectedFields.length === 0 ? (
              <li className="text-ink-500">{t('csv.preview.noStdCols')}</li>
            ) : (
              detectedFields.map(({ field, header }) => (
                <li key={field} className="flex items-center justify-between gap-3">
                  <span className="text-ink-700">{field}</span>
                  <span className="text-ink-500">→ "{header}"</span>
                </li>
              ))
            )}
          </ul>
        </section>

        {willInsert > 0 && (
          <section>
            <SectionLabel>{t('csv.preview.newClients', { count: willInsert })}</SectionLabel>
            <PreviewList items={state.newClients.slice(0, 8)} />
            {willInsert > 8 && (
              <p className="text-[12px] text-ink-500 mt-1">
                {t('csv.preview.moreOnConfirm', { extra: willInsert - 8 })}
              </p>
            )}
          </section>
        )}

        {willSkipDup > 0 && (
          <section>
            <SectionLabel>{t('csv.preview.dupHeader', { count: willSkipDup })}</SectionLabel>
            <p className="text-[12px] text-ink-500 mb-2">{t('csv.preview.dupSub')}</p>
            <PreviewList items={state.duplicates.slice(0, 5)} dim />
            {willSkipDup > 5 && (
              <p className="text-[12px] text-ink-500 mt-1">
                {t('csv.preview.dupMore', { extra: willSkipDup - 5 })}
              </p>
            )}
          </section>
        )}

        {willSkipBlank > 0 && (
          <section>
            <SectionLabel>{t('csv.preview.blankHeader', { count: willSkipBlank })}</SectionLabel>
            <p className="text-[12px] text-ink-500">
              {t('csv.preview.blankBody', {
                rows: state.skipped.slice(0, 10).join(', ') + (state.skipped.length > 10 ? '…' : ''),
              })}
            </p>
          </section>
        )}
      </div>

      <div className="shrink-0 border-t border-line p-4 flex gap-2 bg-bg-alt">
        <button
          type="button"
          onClick={onConfirm}
          disabled={importing || willInsert === 0}
          className="flex-1 h-11 rounded-lg bg-navy-900 text-white dark:bg-gold-500 dark:text-navy-900 font-semibold disabled:opacity-50"
        >
          {importing
            ? t('csv.preview.importing')
            : willInsert === 0
            ? t('csv.preview.nothingToImport')
            : t(willInsert === 1 ? 'csv.preview.importBtn_one' : 'csv.preview.importBtn_other', {
                count: willInsert,
              })}
        </button>
        <button
          type="button"
          onClick={onCancel}
          className="px-5 h-11 rounded-lg border border-line bg-surface font-semibold text-ink-700"
        >
          {t('csv.preview.cancel')}
        </button>
      </div>
      </div>
    </ModalShell>
  );
}

function PreviewList({ items, dim }: { items: NewClientPreview[]; dim?: boolean }) {
  const t = useT();
  return (
    <ul className={`divide-y divide-line rounded-lg border border-line bg-surface ${dim ? 'opacity-60' : ''}`}>
      {items.map((c) => {
        const name = `${c.first_name} ${c.last_name}`.trim() || t('today.unnamed');
        const bday = formatBirthdayCell(c.birthday_month, c.birthday_day);
        return (
          <li key={c.rowNum} className="px-3 py-2 flex items-center gap-3 text-sm">
            <span className="text-[10px] text-ink-500 tabular-nums w-8 shrink-0">
              #{c.rowNum}
            </span>
            <strong className="flex-1 truncate">{name}</strong>
            {/* Birthday chip — only renders when the row had a parseable
                mm/dd. Reassures the user the column was detected (and
                that any year in the source was stripped, per policy). */}
            {bday && (
              <span className="text-[10px] font-bold tracking-wide uppercase text-navy-900 dark:text-gold-500 bg-bg-alt rounded px-1.5 py-0.5">
                {t('csv.preview.birthdayBadge', { date: bday })}
              </span>
            )}
            {c.phone && <span className="text-ink-500 text-[12px] tabular-nums">{c.phone}</span>}
          </li>
        );
      })}
    </ul>
  );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="text-[11px] tracking-[0.14em] uppercase font-extrabold text-ink-500">
      {children}
    </div>
  );
}

function UploadIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="17 8 12 3 7 8" />
      <line x1="12" y1="3" x2="12" y2="15" />
    </svg>
  );
}

function DownloadIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </svg>
  );
}
